import { selectHasMatch, selectMatch, selectQueryParam } from 'util/selector/routerSelector';

import { AppState } from 'reduxStore/appStore';
import { createSelector } from '@reduxjs/toolkit';
import { AppRoute } from 'app/route/app';
import {
  ProjectKind,
  PULSE_TEMPLATE_CHILD_PROJECT_KIND_LIST,
  PulseTemplateChildProjectKind,
  REGULAR_PROJECT_KIND_LIST,
} from 'model/ProjectKind';
import { Project } from 'model/Project';
import { Question } from 'model/Question';
import { PulseFrequency } from 'model/PulseFrequency';
import { PulseNavigationOption, PulseNavigationOptionV2 } from 'model/Project.model';
import dayjs from 'dayjs';
import { QueryType } from 'model/QueryType';
import { SentimentState } from 'model/Sentiment';
import { LoadingStateEnum } from 'model/Resource';
import { Lifecycle, LifecycleType } from 'model/Lifecycle';

import { ProjectState, STORE_NAME } from './initialState';

// Fallback for old API responses that omit lifecycleType — derive it from the project name instead.
const NAME_TO_LIFECYCLE_TYPE: Record<string, LifecycleType> = {
  [Lifecycle.LIFECYCLE_LABEL[LifecycleType.ONBOARD]]: LifecycleType.ONBOARD,
  [Lifecycle.LIFECYCLE_LABEL[LifecycleType.EXIT]]: LifecycleType.EXIT,
};

export function selectDomain(state: AppState | undefined): ProjectState.Domain {
  return state?.[STORE_NAME] || ProjectState.INITIAL_DOMAIN;
}

export const selectProjectList = createSelector(selectDomain, (domain) => domain?.list);

export const selectCurrentProjectResource = createSelector(
  selectDomain,
  (domain) => domain.current
);

export const selectCurrentProject = createSelector(selectCurrentProjectResource, (resource) => {
  return resource.data || ProjectState.INITIAL_DOMAIN.current.data;
});

export const selectCurrentProjectIdFromState = createSelector(
  selectCurrentProject,
  (currentProject) => currentProject?.id
);

export const selectIsCurrentProjectResolved = createSelector(
  selectCurrentProjectResource,
  (resource) => resource.loadingState === LoadingStateEnum.RESOLVED
);

export const selectPagination = createSelector(selectDomain, (domain) => domain?.pagination);

export const selectHasProjects = createSelector(
  selectProjectList,
  (projects) => projects.data?.length !== 0
);

// TODO: Separate below selector for 2 different selectors, one from the url, another from the state. https://buzzing.atlassian.net/browse/WB3-1736
export const selectCurrentProjectId = createSelector(
  selectMatch(AppRoute.PROJECT_PATTERN),
  selectQueryParam<string>(QueryType.ProjectId),
  selectCurrentProject,
  (match: any, projectIdFromQueryParam: any, currentProject: any) => {
    const urlId = match?.params?.id ? Number(match.params.id) : NaN;
    const projectIdFromQueryParamCasted = projectIdFromQueryParam
      ? Number(projectIdFromQueryParam)
      : NaN;

    if (!isNaN(urlId)) {
      return urlId;
    }

    if (!isNaN(projectIdFromQueryParamCasted)) {
      return projectIdFromQueryParamCasted;
    }

    return currentProject.id;
  }
);

export const selectProjectIdParam = createSelector(selectQueryParam('projectId'), (projectId) => {
  return Number(projectId);
});

export const selectCurrentProjectName = createSelector(
  selectCurrentProject,
  (project) => project?.name
);

export const selectCurrentCompanyName = createSelector(
  selectCurrentProject,
  (project) => project?.companyName
);

export const selectRespondentProtection = createSelector(
  selectCurrentProject,
  (project) => project?.settings?.respondentProtection
);

export const selectAggregationMonths = createSelector(
  selectCurrentProject,
  (project) => project?.settings?.aggregationMonths || ProjectState.DEFAULT_AGGREGATION_MONTHS
);

export const selectCompanyLogoUrl = createSelector(
  selectCurrentProject,
  (project) => project?.settings?.logo
);

export const selectMinResponsesCloseQuestion = createSelector(
  selectRespondentProtection,
  (responses) => responses?.closed
);

export const selectProjectClosedAt = createSelector(selectCurrentProject, (project) => {
  if ('closedAt' in project) {
    return project.closedAt;
  }
  console.error('Something is wrong, you are trying to access field which does not exist');
  return dayjs();
});

export const selectQuestionsStats = createSelector(
  selectDomain,
  (domain) => domain.questionsStats?.data
);

export const selectIsQuestionsStatsLoading = createSelector(
  selectDomain,
  (domain) => domain.questionsStats?.loadingState === LoadingStateEnum.PENDING
);

export const selectHasCommentQuestion = createSelector(
  selectQuestionsStats,
  (stats): boolean => !!(stats && Question.hasCommentsQuestion(stats))
);

export const selectHasInsightQuestion = createSelector(
  selectQuestionsStats,
  (stats) => !!(stats && Question.hasInsightsQuestion(stats))
);

export const selectProjectKindResource = createSelector(selectDomain, (domain) => domain.kind);

export const selectProjectKind = createSelector(
  selectProjectKindResource,
  selectCurrentProject,
  (projectKindResource, currentProject) => projectKindResource?.data || currentProject.kind
);

export const selectIsProjectKindLoading = createSelector(
  selectProjectKindResource,
  (resource) => resource?.loadingState === LoadingStateEnum.PENDING
);

export const selectIsRegularProjectKind = createSelector(selectProjectKind, (kind) => {
  return REGULAR_PROJECT_KIND_LIST.some((expectedKind) => expectedKind === kind);
});

export const selectIsPulseProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind && kind === ProjectKind.PULSE_PROJECT
);

export const selectIsEngagementProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind && kind === ProjectKind.PROJECT_ENGAGEMENT_SCORE
);

export const selectIsPulseProjectFamilyKind = createSelector(selectProjectKind, (kind) =>
  [
    ProjectKind.PULSE_PROJECT,
    ProjectKind.PULSE_ON_DEMAND_PROJECT,
    ProjectKind.PULSE_TEMPLATE,
  ].includes(kind)
);

export const selectIsPulseTemplateChildProjectKind = createSelector(
  selectProjectKind,
  (kind) =>
    kind !== null &&
    PULSE_TEMPLATE_CHILD_PROJECT_KIND_LIST.includes(kind as PulseTemplateChildProjectKind)
);

export const selectIsPulseTemplateProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind && kind === ProjectKind.PULSE_TEMPLATE
);

export const selectIsPulseOrTemplateProjectKind = createSelector(selectProjectKind, (kind) => {
  return kind && [ProjectKind.PULSE_TEMPLATE, ProjectKind.PULSE_PROJECT].includes(kind);
});

export const selectIsOnDemandProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind === ProjectKind.PULSE_ON_DEMAND_PROJECT
);

export const selectIsLifecycleProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind === ProjectKind.PULSE_LIFECYCLE_PROJECT
);

export const selectIsEngagementScoreCompatibleProject = createSelector(
  selectProjectKind,
  (kind) =>
    kind === ProjectKind.PROJECT_ENGAGEMENT_SCORE ||
    kind === ProjectKind.PULSE_PROJECT ||
    kind === ProjectKind.PULSE_TEMPLATE
);

export const selectIsCasProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind === ProjectKind.PROJECT_CLIENT_ADVOCACY_SCORE
);

export const selectIsOverallProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind === ProjectKind.PROJECT_OVERALL_SCORE
);

export const selectRegularProjectId = createSelector(
  selectCurrentProject,
  selectIsRegularProjectKind,
  (project, isRegularProject) => (isRegularProject ? project?.id : undefined)
);

export const selectProjectSettings = createSelector(
  selectCurrentProject,
  (project) => project?.settings
);

export const selectIsOverallScoreShow = createSelector(
  selectProjectSettings,
  (settings) => !!settings?.isOverallScoreShow
);

export const selectShowConsentQuestion = createSelector(
  selectProjectSettings,
  (settings) => settings?.showConsentQuestion
);

export const selectIsOnPageWithCloseQuestion = createSelector(
  selectHasMatch(AppRoute.Home.SUMMARY),
  selectHasMatch(AppRoute.INSIGHTS),
  (isOnSummaryPage, isOnInsightPage) => isOnSummaryPage || isOnInsightPage
);

export const selectRespondentProtectionNumber = createSelector(
  selectRespondentProtection,
  selectIsOnPageWithCloseQuestion,
  (respondent, isOnPageWithCloseQuestions) =>
    respondent && (isOnPageWithCloseQuestions ? respondent.closed : respondent.comment)
);

export const selectPulseTemplateId = createSelector(selectCurrentProject, (project) => {
  if (project.kind === ProjectKind.PULSE_TEMPLATE) {
    return project.id;
  }
  return project.pulseTemplateId;
});

export const selectHasMoreThanOneProject = createSelector(
  selectProjectList,
  (projects) => projects.data.length > 1
);

export const selectReportTitle = createSelector(
  selectCurrentProject,
  (project) => project && Project.getReportTitle(project)
);

export const selectReportAvailabilities = createSelector(
  selectDomain,
  (project) => project?.reportAvailabilities
);

export const selectIsSentimentAnalysisEnabled = createSelector(
  selectProjectSettings,
  (settings) => settings?.sentimentAnalysis
);

export const selectReportAvailability = (departmentId: number) =>
  createSelector(selectReportAvailabilities, (reportAvailabilities) =>
    reportAvailabilities.data?.find(
      (reportAvailability) => reportAvailability.departmentId === departmentId
    )
  );

export const selectProjectPulses = createSelector(selectCurrentProject, (project) => {
  return Project.isPulseTemplateOrOneOfChild(project) ? project.pulseProjects : [];
});

export const selectProjectOnDemandPulses = createSelector(selectCurrentProject, (project) => {
  return Project.isPulseTemplateOrOneOfChild(project) ? project.pulseOnDemandProjects : [];
});

export const selectIsLatestPulseProject = createSelector(
  selectCurrentProject,
  selectCurrentProjectId,
  (project, currentProjectId): boolean => {
    if (!Project.isPulseTemplateOrOneOfChild(project)) {
      return true;
    }
    const { latestPulseId } = Project.getAvailableProjectOptions(project);
    if (!latestPulseId) return true;
    return currentProjectId === latestPulseId;
  }
);

export const selectSentimentIsLoaded = createSelector(
  selectDomain,
  (domain) => domain.sentiment?.data === SentimentState.Loaded
);

export const selectCurrentProjectIsHighPulseFrequency = createSelector(
  selectCurrentProject,
  (project) => {
    if ('pulseFrequencyId' in project && project.pulseFrequencyId !== null) {
      return [PulseFrequency.weekly, PulseFrequency.biWeekly, PulseFrequency.monthly].includes(
        project.pulseFrequencyId
      );
    }
    return false;
  }
);

/**
 * Used to select all project user can navigate to while visiting any  pulse project child (ODP, lifecycle pulse)
 */
export const selectPulseProjectAvailableProjectsOptions = createSelector(
  selectCurrentProject,
  (project): PulseNavigationOption[] => {
    const options: PulseNavigationOption[] = [];

    if (!Project.isPulseTemplateOrOneOfChild(project)) {
      return options;
    }

    const { latestPulseId, latestOnDemandId } = Project.getAvailableProjectOptions(project);

    if (latestPulseId) {
      options.push({
        label: 'Engage (Pulse Surveys)',
        projectId: latestPulseId,
        kind: ProjectKind.PULSE_PROJECT,
      });
    }

    if (latestOnDemandId) {
      options.push({
        label: 'On Demand Polls',
        projectId: latestOnDemandId,
        kind: ProjectKind.PULSE_ON_DEMAND_PROJECT,
      });
    }

    if (project.pulseLifecycleProjects.length > 0) {
      const byType = new Map<LifecycleType, typeof project.pulseLifecycleProjects>();
      project.pulseLifecycleProjects.forEach((p) => {
        const effectiveType = p.lifecycleType ?? NAME_TO_LIFECYCLE_TYPE[p.name];
        if (!effectiveType) return;
        byType.set(effectiveType, [...(byType.get(effectiveType) ?? []), p]);
      });
      byType.forEach((pulses, type) => {
        const sorted = [...pulses].sort(
          (a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0)
        );
        options.push({
          label: Lifecycle.LIFECYCLE_LABEL[type],
          projectId: sorted[0].id,
          kind: ProjectKind.PULSE_LIFECYCLE_PROJECT,
        });
      });
    }

    return options;
  }
);

/**
 * Used to select all project user can navigate to while visiting any  pulse project child (ODP, lifecycle pulse) V2
 */
export const selectPulseProjectAvailableProjectsOptionsV2 = createSelector(
  selectCurrentProject,
  (project): PulseNavigationOptionV2[] => {
    const options: PulseNavigationOptionV2[] = [];

    if (!Project.isPulseTemplateOrOneOfChild(project)) {
      return options;
    }

    const { latestPulseId, latestOnDemandId } = Project.getAvailableProjectOptions(project);

    if (latestPulseId) {
      options.push({
        label: 'Pulse',
        projectId: latestPulseId,
        kind: ProjectKind.PULSE_PROJECT,
      });
    }

    if (project.pulseLifecycleProjects.length > 0) {
      const byType = new Map<LifecycleType, typeof project.pulseLifecycleProjects>();
      project.pulseLifecycleProjects.forEach((p) => {
        const effectiveType = p.lifecycleType ?? NAME_TO_LIFECYCLE_TYPE[p.name];
        if (!effectiveType) return;
        byType.set(effectiveType, [...(byType.get(effectiveType) ?? []), p]);
      });
      byType.forEach((pulses, type) => {
        const sorted = [...pulses].sort(
          (a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0)
        );
        options.push({
          label: Lifecycle.LIFECYCLE_LABEL[type],
          projectId: sorted[0].id,
          kind: ProjectKind.PULSE_LIFECYCLE_PROJECT,
        });
      });
    }

    if (latestOnDemandId) {
      options.push({
        label: 'On Demand',
        projectId: latestOnDemandId,
        kind: ProjectKind.PULSE_ON_DEMAND_PROJECT,
      });
    }

    return options;
  }
);

// When viewing not the latest ODP/pulse, it's ID is not in any of the options.
// Setting (options.find || currentProjectId) so that Select component doesn't get created uncontrolled
export const selectCurrentPulseProjectAvailableProjectsOptionId = createSelector(
  selectProjectKind,
  selectCurrentProjectId,
  selectPulseProjectAvailableProjectsOptions,
  (projectKind, currentProjectId, options) =>
    projectKind === ProjectKind.PULSE_LIFECYCLE_PROJECT
      ? currentProjectId
      : options.find((option) => option.kind === projectKind)?.projectId || currentProjectId
);

export const selectIsPulseLifecycleProjectKind = createSelector(
  selectProjectKind,
  (kind) => kind === ProjectKind.PULSE_LIFECYCLE_PROJECT
);

export const selectCurrentProjectLifecycleType = createSelector(selectCurrentProject, (project) => {
  if (project?.kind && project.kind !== ProjectKind.PULSE_LIFECYCLE_PROJECT) {
    throw new Error(`Project which you use is not ${ProjectKind.PULSE_LIFECYCLE_PROJECT}`);
  }
  return project.lifecycleType;
});

export const selectCurrentProjectLifecycleTypeV2 = createSelector(
  selectCurrentProject,
  (project) => {
    let lifecycleType;
    if (project?.kind && project.kind !== ProjectKind.PULSE_LIFECYCLE_PROJECT) {
      lifecycleType = null;
    } else {
      lifecycleType = project.lifecycleType;
    }
    return lifecycleType;
  }
);

export const selectCurrentTypeLifecycleProjects = createSelector(
  [selectCurrentProject, selectCurrentProjectLifecycleTypeV2],
  (project, lifecycleType) => {
    if (!lifecycleType || !project?.pulseLifecycleProjects?.length) return [];
    return [...project.pulseLifecycleProjects]
      .filter((p) => (p.lifecycleType ?? NAME_TO_LIFECYCLE_TYPE[p.name]) === lifecycleType)
      .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));
  }
);

export const selectProjectLiveSettingsResource = createSelector(
  selectDomain,
  (domain) => domain.liveSettings
);

export const selectProjectLiveSettings = createSelector(
  selectProjectLiveSettingsResource,
  (liveSettingsResource) => liveSettingsResource?.data
);

export const selectIsProjectLiveSettingsResolved = createSelector(
  selectProjectLiveSettingsResource,
  (resource) => resource?.loadingState === LoadingStateEnum.RESOLVED
);

export const selectAvailableLanguages = createSelector(
  selectProjectLiveSettings,
  (liveSettings) => liveSettings?.languageCodes || []
);

export const selectShouldTranslateComments = createSelector(
  selectProjectLiveSettings,
  (liveSettings) => liveSettings?.translateComments || false
);

export const selectAreDashboardLanguagesEnabled = createSelector(
  selectProjectLiveSettings,
  (liveSettings) => liveSettings?.dashboardLanguagesEnabled || false
);

// Fails open (defaults to enabled) while live settings are unresolved, matching the other
// ACT-visibility gates in this codebase — only an explicit `false` from the BE hides ACT.
export const selectIsActEnabled = createSelector(
  selectProjectLiveSettings,
  (liveSettings) => liveSettings?.isActEnabled ?? true
);

// `undefined` while live settings are unresolved means "no limit yet" — callers should show
// everything rather than flashing an empty list until the real limit loads.
export const selectMaxFocusAreas = createSelector(
  selectProjectLiveSettings,
  (liveSettings) => liveSettings?.maxFocusAreas
);

export const selectMaxActionsPerFocusArea = createSelector(
  selectProjectLiveSettings,
  (liveSettings) => liveSettings?.maxActionsPerFocusArea
);

// Fails closed (defaults to disabled) while live settings are unresolved — only an explicit
// `true` from the BE unlocks the AI-suggested-actions buttons in Build Your Action Plan.
export const selectHasCompanyAiSummariesEnabled = createSelector(
  selectProjectLiveSettings,
  (liveSettings) => liveSettings?.hasCompanyAiSummariesEnabled ?? false
);

export const selectFocusAreasResource = createSelector(selectDomain, (domain) => domain.focusAreas);

export const selectFocusAreas = createSelector(
  selectFocusAreasResource,
  (resource) => resource?.data
);

export const selectIsFocusAreasLoading = createSelector(
  selectFocusAreasResource,
  (resource) => resource?.loadingState === LoadingStateEnum.PENDING
);
